import type { TenantsPublic, UsersPublic } from "@common/client"
import { TenantService } from "@common/client"
import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { currentUserKey } from "./users"
import {
  makeAxiosMutation,
  makeNoArgsQuery,
  makeQuery,
  parseBlobErrors,
  unwrap,
} from "./utils"

export const tenantsInfiniteBaseKey = ["tenants-infinite"] as const

export const tenantsInfiniteKey = <T>(search: string, filters: T) =>
  [...tenantsInfiniteBaseKey, search, filters] as const

export const tenantBaseKey = ["tenant"] as const

export const tenantKey = (tenantId: string) =>
  [...tenantBaseKey, tenantId] as const

export const tenantQuery = makeQuery({
  serviceCall: TenantService.getTenant,
  queryKey: (params) => tenantKey(params.tenant_id),
})

export const currentTenantKey = ["current-tenant"] as const

export const currentTenantQuery = makeNoArgsQuery({
  serviceCall: TenantService.getCurrentTenant,
  queryKey: currentTenantKey,
})

export const useUpdateCurrentTenantMutation = makeAxiosMutation({
  serviceCall: TenantService.updateCurrentTenant,
  invalidates: () => [currentTenantKey, tenantBaseKey],
})

export const useUploadCurrentCompanyLogoMutation = makeAxiosMutation({
  serviceCall: TenantService.uploadCurrentCompanyLogo,
  invalidates: () => [currentTenantKey, tenantBaseKey, currentUserKey],
})

export const useDeleteCurrentCompanyLogoMutation = makeAxiosMutation({
  serviceCall: TenantService.deleteCurrentCompanyLogo,
  invalidates: () => [currentTenantKey, tenantBaseKey, currentUserKey],
})

export const tenantUsersInfiniteBaseKey = (tenantId: string) =>
  ["tenant-users-infinite", tenantId] as const

export const tenantUsersInfiniteKey = <T>(
  tenantId: string,
  search: string,
  filters: T,
) => [...tenantUsersInfiniteBaseKey(tenantId), search, filters] as const

export const tenantOwnerKey = (tenantId: string) =>
  ["tenant-owner", tenantId] as const

export const tenantOwnerQuery = makeQuery({
  serviceCall: TenantService.getTenantOwner,
  queryKey: (params) => tenantOwnerKey(params.tenant_id),
})

export const tenantRolesKey = (tenantId: string) =>
  ["tenant-roles", tenantId] as const

export const tenantRolesQuery = makeQuery({
  serviceCall: TenantService.listRoles,
  queryKey: (params) => tenantRolesKey(params.tenant_id),
})

export const useCreateTenantMutation = makeAxiosMutation({
  serviceCall: TenantService.createTenant,
  invalidates: () => [tenantsInfiniteBaseKey],
})

export const useUpdateTenantMutation = makeAxiosMutation({
  serviceCall: TenantService.updateTenant,
  invalidates: (params) => [
    tenantsInfiniteBaseKey,
    tenantKey(params.tenant_id),
    currentTenantKey,
  ],
})

export const useAssignTenantOwnerMutation = makeAxiosMutation({
  serviceCall: TenantService.assignTenantOwner,
  invalidates: (params) => [tenantUsersInfiniteBaseKey(params.tenant_id)],
})

export const useCreateMultipleUsersMutation = makeAxiosMutation({
  serviceCall: TenantService.createMultipleUsers,
  invalidates: (params) => [
    tenantUsersInfiniteBaseKey(params.tenant_id),
    tenantOwnerKey(params.tenant_id),
  ],
})

export const useUpdateTenantUserMutation = makeAxiosMutation({
  serviceCall: TenantService.updateUserByTenantId,
  invalidates: (params) => [tenantUsersInfiniteBaseKey(params.tenant_id)],
})

export const useResendTenantUserLoginEmailMutation = makeAxiosMutation({
  serviceCall: TenantService.resendLoginEmailByTenantId,
  invalidates: (params) => [tenantUsersInfiniteBaseKey(params.tenant_id)],
})

export const useExportTenantUsersMutation = makeAxiosMutation({
  serviceCall: parseBlobErrors(
    (params: Parameters<typeof TenantService.exportUsersByTenantId>[0]) =>
      TenantService.exportUsersByTenantId(params, { responseType: "blob" }),
  ),
  invalidates: () => [],
})

export const useDeleteTenantUserMutation = makeAxiosMutation({
  serviceCall: TenantService.deleteUserByTenantId,
  invalidates: (params) => [tenantUsersInfiniteBaseKey(params.tenant_id)],
})

export const useUploadCompanyLogoMutation = makeAxiosMutation({
  serviceCall: TenantService.uploadCompanyLogo,
  invalidates: (params) => [
    tenantKey(params.tenant_id),
    currentTenantKey,
    currentUserKey,
  ],
})

export const useDeleteCompanyLogoMutation = makeAxiosMutation({
  serviceCall: TenantService.deleteCompanyLogo,
  invalidates: (params) => [
    tenantKey(params.tenant_id),
    currentTenantKey,
    currentUserKey,
  ],
})

// --- Infinite query hooks ---

const TENANTS_PER_PAGE = 20
const TENANT_USERS_PER_PAGE = 20

export interface TenantFilters {
  type?: string
  is_active?: boolean
}

export function useTenants(search: string, filters: TenantFilters) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useInfiniteQuery<
    TenantsPublic,
    Error,
    InfiniteData<TenantsPublic>,
    ReturnType<typeof tenantsInfiniteKey>,
    number
  >({
    queryKey: tenantsInfiniteKey(search, filters),
    queryFn: ({ pageParam }) =>
      TenantService.getTenants({
        skip: pageParam,
        limit: TENANTS_PER_PAGE,
        search: search || undefined,
        type: filters.type || undefined,
        is_active: filters.is_active,
      }).then(unwrap),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * TENANTS_PER_PAGE
      return loaded < (lastPage.count ?? 0) ? loaded : undefined
    },
    initialPageParam: 0,
    staleTime: 30_000,
  })

  const tenants = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  )

  const totalCount = data?.pages[0]?.count ?? 0

  return {
    tenants,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  }
}

export interface TenantUserFilters {
  designation?: string
  is_system_user?: boolean
  is_active?: boolean
  role_ids?: string[]
}

export function tenantUsersParams(
  tenantId: string,
  search: string,
  filters: TenantUserFilters,
): Omit<
  Parameters<typeof TenantService.readUsersByTenantId>[0],
  "skip" | "limit"
> {
  return {
    tenant_id: tenantId,
    search: search || undefined,
    designation: filters.designation || undefined,
    is_system_user: filters.is_system_user,
    is_active: filters.is_active,
    role_ids: filters.role_ids?.length ? filters.role_ids : undefined,
  }
}

export function useTenantUsers(
  tenantId: string,
  search: string,
  filters: TenantUserFilters,
  enabled: boolean,
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useInfiniteQuery<
    UsersPublic,
    Error,
    InfiniteData<UsersPublic>,
    ReturnType<typeof tenantUsersInfiniteKey>,
    number
  >({
    queryKey: tenantUsersInfiniteKey(tenantId, search, filters),
    queryFn: ({ pageParam }) =>
      TenantService.readUsersByTenantId({
        ...tenantUsersParams(tenantId, search, filters),
        skip: pageParam,
        limit: TENANT_USERS_PER_PAGE,
      }).then(unwrap),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * TENANT_USERS_PER_PAGE
      return loaded < (lastPage.count ?? 0) ? loaded : undefined
    },
    initialPageParam: 0,
    enabled,
    staleTime: 30_000,
  })

  const users = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data])

  const totalCount = data?.pages[0]?.count ?? 0

  return {
    users,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  }
}
